import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchBranches } from "../../../api/users";
import {
  createExpenseCategory,
  fetchChartOfAccounts,
  fetchCostCenters,
  fetchExpenseCategories,
  updateExpenseCategory,
} from "../../../api/accounting";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { useLatestRef } from "./accountingUtils";

const ExpenseCategoriesPage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", account_id: "", cost_center_id: "" });

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  const loadMeta = useCallback(async () => {
    const [a, c] = await Promise.all([
      fetchChartOfAccounts({ branch_id: branchId || undefined, account_type: "expense", active_only: true }),
      fetchCostCenters(branchId || undefined),
    ]);
    setAccounts(a || []);
    setCostCenters(c || []);
  }, [branchId]);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return runLoad(
      () => Promise.all([fetchExpenseCategories(branchId || undefined).then(setRows), loadMeta()]),
      { errorMessage: "Failed to load expense categories." }
    )
      .catch((err) => setError(formatApiError(err, "Failed to load expense categories.")))
      .finally(() => setLoading(false));
  }, [branchId, loadMeta]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRef = useLatestRef(load);

  const setActive = useCallback(
    (row, isActive) => {
      confirm({
        title: isActive ? `Reactivate ${row.code}?` : `Deactivate ${row.code}?`,
        confirmLabel: isActive ? "Reactivate" : "Deactivate",
        tone: isActive ? "primary" : "danger",
        successMessage: isActive ? "Category reactivated." : "Category deactivated.",
        onConfirm: async () => {
          await updateExpenseCategory(row.id, { is_active: isActive });
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const columns = useMemo(
    () => [
      { name: "Code", selector: (r) => r.code, width: "100px" },
      { name: "Name", selector: (r) => r.name, grow: 2 },
      { name: "GL Account", selector: (r) => `${r.account_code || ""} ${r.account_name || ""}`.trim(), grow: 2 },
      { name: "Cost center", selector: (r) => r.cost_center_name || "—", width: "140px" },
      {
        name: "Status",
        cell: (r) => <Badge color={r.is_active ? "success" : "secondary"}>{r.is_active ? "Active" : "Inactive"}</Badge>,
        width: "100px",
      },
      {
        name: "Actions",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              {
                icon: r.is_active ? "clear" : "plus",
                label: r.is_active ? "Deactivate category" : "Activate category",
                onClick: () => setActive(r, !r.is_active),
                variant: r.is_active ? "danger" : "primary",
              },
            ]}
          />
        ),
        width: "56px",
        ignoreRowClick: true,
      },
    ],
    [setActive]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () =>
          createExpenseCategory({
            branch_id: branchId || null,
            code: form.code,
            name: form.name,
            account_id: form.account_id,
            cost_center_id: form.cost_center_id || null,
          }),
        { successMessage: "Expense category created." }
      );
      setModalOpen(false);
      setForm({ code: "", name: "", account_id: "", cost_center_id: "" });
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Expense Categories" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="mb-0">Categories</h5>
            <div className="d-flex gap-2">
              <Input type="select" bsSize="sm" style={{ width: "180px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">All branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </Input>
              <Button color="primary" size="sm" onClick={() => setModalOpen(true)}>New category</Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : (
              <DataTable columns={columns} data={rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>

        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>New expense category</ModalHeader>
            <ModalBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Code</Label>
                    <Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Name</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>Expense GL account</Label>
                    <Input type="select" required value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })}>
                      <option value="">Select account</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>Default cost center (optional)</Label>
                    <Input type="select" value={form.cost_center_id} onChange={(e) => setForm({ ...form, cost_center_id: e.target.value })}>
                      <option value="">None</option>
                      {costCenters.map((c) => (
                        <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>{saving ? "Creating…" : "Create"}</Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default ExpenseCategoriesPage;
